import React, {useReducer, useState} from 'react';
import {Helmet} from 'react-helmet-async';
import {ContentAdDesktop} from 'app/styles/content';
import {MatchRow} from 'app/components/match/matchRow/MatchRow';
import {Subheading} from 'app/styles/heading';
import {formatDateWithWeekday} from 'app/helpers/dateHelpers';
import {object, string} from 'prop-types';
import {fetchLeagueMatches, overwriteMatches} from 'app/routes/league/LeaguePageActions';
import {LoadingIndicator, LOADING_MESSAGE} from '@fupa/fupa-uikit';
import {StreamPlaceholder} from 'app/styles/stream';
import {useLocation, useParams} from 'react-router';
import {useFetchData, useRestoreData, useEffectAfterMount, useDataFetchFinished} from 'app/hooks/dataHooks';
import {LeagueMatchesPageReducer} from 'app/routes/league/LeaguePageReducer';
import queryString from 'query-string';
import {useInfiniteScrollComponentState} from 'app/hooks/useInfiniteScroll';
import {getPointer} from 'app/helpers/getPointer';
import {ButtonGroup} from 'app/components/button/ButtonGroup';
import {AdSlot} from 'app/components/ads/AdSlot';
import {MatchesPagesWrapper} from 'app/components/match/matchRow/MatchRowStyles';
import StickyMobileBannerAdSlot from 'app/components/ads/StickyMobileBannerAdSlot';

const LeagueMatchesPage = ({defaultTitle, districtName, leagueActive, baseUrl, dispatchRedux}) => {
  const {restoredData} = useRestoreData('LeagueMatchesPage');
  const {leagueSlug, seasonSlug} = useParams();
  const matchesBaseUrl = `${baseUrl}/matches`;
  const location = useLocation();
  const search = location.search;
  const searchQuery = queryString.parse(search);
  const initData = {
    isFetching: null,
    items: [],
    nextUrl: '',
    error: null,
    pointer: '',
  };
  const initialState = restoredData ?? initData;
  const [state, dispatch] = useReducer(LeagueMatchesPageReducer, initialState);
  const {items, isFetching, nextUrl, error} = state;

  const loadingDataFinished = useDataFetchFinished(isFetching);
  const [activeButton, setActiveButton] = useState(getPointer(searchQuery));

  const loadData = (reloading, reset) => {
    const componentState = reset ? initData : state;
    const pointerQuery = getPointer(searchQuery);
    return fetchLeagueMatches(leagueSlug, seasonSlug, pointerQuery, reloading, dispatchRedux)(dispatch, componentState);
  };
  const data = {items, nextUrl};

  useInfiniteScrollComponentState(loadData, items.length);
  useFetchData(state, loadData, 'LeagueMatchesPage', true);

  const robots = leagueActive ? 'index, nofollow' : 'noindex, follow';
  const title = 'Spiele - ';
  const meta = [
    {
      name: 'description',
      content: `Alle Spiele, Ergebnisse, Termine, Anstoßzeiten, Begegnungen, der komplette Spielplan, alle Spieltage auf einen Blick im Kalender der ${defaultTitle} - Kreis ${districtName}.`,
    },
    {name: 'robots', content: robots},
  ];

  const canonical = searchQuery?.pointer ? (
    <link rel='canonical' href={`https://www.fupa.net${matchesBaseUrl}`} />
  ) : null;

  useEffectAfterMount(() => {
    // Request data on switching matches by navigation with toggle buttons
    const pointer = getPointer(searchQuery);
    setActiveButton(pointer);
    dispatch(overwriteMatches(initData));
    loadData(null, true);
  }, [location]);

  const showLoadingMatchesNext = isFetching ? (
    <LoadingIndicator message={LOADING_MESSAGE.loadingMatches} bgColor={false} />
  ) : error ? (
    <StreamPlaceholder>Beim Laden ist ein Fehler aufgetreten.</StreamPlaceholder>
  ) : null;

  const rows = matches => {
    return matches.map(match => {
      return <MatchRow key={match.slug} match={match} colored={false} />;
    });
  };

  const activeButtonText = activeButton === 'prev' ? 'vergangenen' : 'aktuellen';

  const matchDays = data.items.map(day => {
    if (day.day) {
      return (
        <MatchesPagesWrapper key={day.day + day.matches[0].slug} id={day.day}>
          <Subheading variant='subheading2' center={true}>
            {formatDateWithWeekday(day.day)}
          </Subheading>
          {rows(day.matches)}
        </MatchesPagesWrapper>
      );
    } else {
      return day.matches.length ? (
        <MatchesPagesWrapper key={'no-date'}>{rows(day.matches)}</MatchesPagesWrapper>
      ) : (
        <StreamPlaceholder>Keine {activeButtonText} Spiele verfügbar.</StreamPlaceholder>
      );
    }
  });

  if (loadingDataFinished) {
    let sum = 0;
    const matchCountBlock1 = data.items[0]?.matches.length;

    const lengthOfMatchesPerBlock = data.items?.map(item => {
      return item.matches.length;
    });
    const indexOfBlockForRect1 = lengthOfMatchesPerBlock.findIndex(length => {
      sum += length;
      return sum > 5;
    });

    if (matchCountBlock1) {
      matchDays[0].props.children[1].splice(1, 0, <AdSlot key='Content_1' adUnit='Content_1' />);
    }
    if (indexOfBlockForRect1 > -1) {
      const lengthOfRelevantBlock = data.items[indexOfBlockForRect1]?.matches.length;
      const spliceIndexOverhead = sum - lengthOfRelevantBlock;
      const spliceIndex = 7 - spliceIndexOverhead;
      matchDays[indexOfBlockForRect1].props.children[1].splice(
        spliceIndex,
        0,
        <AdSlot key='Rectangle_1' adUnit='Rectangle_1' />
      );
    }
  }

  const buttons = [
    {
      url: matchesBaseUrl,
      label: 'Aktuelle Spiele',
      value: 'current',
    },
    {
      url: `${matchesBaseUrl}?pointer=prev`,
      label: 'Vergangene Spiele',
      value: 'prev',
    },
  ];
  const subNav = leagueActive ? (
    <ButtonGroup activeValue={activeButton} onChange={value => setActiveButton(value)} buttons={buttons} />
  ) : null;

  return (
    <>
      <ContentAdDesktop top='7.125rem' contentPaddingBottom={'4.5rem'}>
        <Helmet title={title} meta={meta}>
          {canonical}
        </Helmet>
        {subNav}
        {matchDays}
        {showLoadingMatchesNext}
      </ContentAdDesktop>
      <StickyMobileBannerAdSlot notOnNative={true} />
    </>
  );
};

LeagueMatchesPage.propTypes = {
  match: object.isRequired,
  defaultTitle: string.isRequired,
  districtName: string.isRequired,
};

export default LeagueMatchesPage;
